import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { QRCode } from 'react-qr-code';
import { X, Download } from 'lucide-react';

/**
 * Componente para mostrar el código QR de un equipo
 * @param {Object} props
 * @param {string} props.equipoId - ID del equipo
 * @param {string} props.equipoNombre - Nombre del equipo
 * @param {string} props.qrCode - Código QR en formato base64 (opcional si viene de backend)
 * @param {boolean} props.isOpen - Si el modal está abierto
 * @param {Function} props.onClose - Función para cerrar el modal
 */
const EquipoQRCode = ({ equipoId, equipoNombre, qrCode, isOpen, onClose }) => {
  const [error, setError] = useState(null);

  // URL completa del equipo
  const equipoUrl = `https://app.elements.com.py/equipos/${equipoId}`;

  /**
   * Descargar el QR code como imagen PNG
   */
  const handleDownload = () => {
    try {
      // Si tenemos el QR en base64 del backend, usarlo
      if (qrCode && qrCode.startsWith('data:image')) {
        const link = document.createElement('a');
        link.href = qrCode;
        link.download = `QR_${equipoNombre || equipoId}.png`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        // Generar desde el SVG en pantalla
        const svg = document.getElementById('equipo-qr-svg');
        if (!svg) {
          setError('No se pudo encontrar el código QR');
          return;
        }

        const svgData = new XMLSerializer().serializeToString(svg);
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        const img = new Image();

        img.onload = () => {
          canvas.width = img.width;
          canvas.height = img.height;
          ctx.drawImage(img, 0, 0);
          const pngFile = canvas.toDataURL('image/png');

          const link = document.createElement('a');
          link.href = pngFile;
          link.download = `QR_${equipoNombre || equipoId}.png`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        };

        img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)));
      }
    } catch (err) {
      console.error('Error al descargar QR:', err);
      setError('Error al descargar el código QR');
    }
  };

  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6 relative">
        {/* Botón cerrar */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
          aria-label="Cerrar"
        >
          <X size={24} />
        </button>

        {/* Título */}
        <h2 className="text-2xl font-bold text-gray-800 mb-2">
          Código QR del Equipo
        </h2>
        <p className="text-sm text-gray-600 mb-6">
          {equipoNombre || equipoId}
        </p>

        {/* Mostrar error si existe */}
        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-600">{error}</p>
          </div>
        )}

        {/* QR Code */}
        <div className="flex justify-center mb-6 p-6 bg-gray-50 rounded-lg">
          {qrCode && qrCode.startsWith('data:image') ? (
            // Mostrar QR desde base64 (backend)
            <img 
              src={qrCode} 
              alt={`QR Code para ${equipoNombre}`}
              className="w-64 h-64"
            />
          ) : (
            // Generar QR con la librería (fallback)
            <QRCode
              id="equipo-qr-svg"
              value={equipoUrl}
              size={256}
              level="H"
            />
          )}
        </div>

        {/* URL del equipo */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            URL del Equipo:
          </label>
          <div className="flex items-center">
            <input
              type="text"
              value={equipoUrl}
              readOnly
              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-sm"
            />
            <button
              onClick={() => {
                navigator.clipboard.writeText(equipoUrl);
                alert('URL copiada al portapapeles');
              }}
              className="ml-2 px-3 py-2 bg-emerald-500 text-white rounded-lg hover:bg-emerald-600 transition-colors text-sm"
            >
              Copiar
            </button>
          </div>
        </div>

        {/* Botones de acción */}
        <div className="flex gap-3">
          <button
            onClick={handleDownload}
            className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors font-medium"
          >
            <Download size={20} />
            Descargar QR
          </button>
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors font-medium"
          >
            Cerrar
          </button>
        </div>

        {/* Instrucciones */}
        <div className="mt-4 p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
          <p className="text-xs text-emerald-800">
            <strong>💡 Consejo:</strong> Escanea este código QR con tu celular para acceder 
            rápidamente a los detalles de este equipo.
          </p>
        </div>
      </div>
    </div>
  , document.body);
};

export default EquipoQRCode;
